import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Scale, Pencil, X, RotateCcw } from "lucide-react";

const REGIMES = [
  { v: "davis-bacon", label: "Davis-Bacon (DBA)", hint: "Construction prevailing wage — requires a wage determination" },
  { v: "sca", label: "Service Contract Act (SCA)", hint: "Service prevailing wage — requires a wage determination" },
  { v: "commercial", label: "Commercial-item", hint: "FAR Part 12 — market labor rates" },
  { v: "none", label: "None", hint: "No wage clause detected — market labor rates" },
];
const LABEL = Object.fromEntries(REGIMES.map((r) => [r.v, r.label]));

const TONE = {
  "davis-bacon": "bg-amber-100 text-amber-800 border-amber-300",
  sca: "bg-amber-100 text-amber-800 border-amber-300",
  commercial: "bg-emerald-100 text-emerald-700 border-emerald-300",
  none: "bg-zinc-100 text-zinc-600 border-zinc-300",
};

export function WageRegimeChip({ deal, isOfficer, reload }) {
  const wr = deal.wage_regime || {};
  const [open, setOpen] = useState(false);
  const [regime, setRegime] = useState(wr.regime || "none");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!wr.regime) return null;

  const isOverride = wr.override;
  const conf = wr.confidence || "";

  const save = async () => {
    if (!reason.trim()) { toast.error("A reason is required (logged to audit)."); return; }
    setBusy(true);
    try {
      await api.put(`/bids/${deal.id}/wage-regime`, { regime, reason });
      toast.success(`Wage regime set to ${LABEL[regime]}`);
      setOpen(false); setReason(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to override"); }
    finally { setBusy(false); }
  };
  const clearOverride = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${deal.id}/wage-regime/clear`, {});
      toast.success("Reverted to auto-detection"); setOpen(false); reload();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 relative inline-block" data-testid="wage-regime-chip">
      <button
        onClick={() => isOfficer && setOpen((o) => !o)}
        disabled={!isOfficer}
        className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${TONE[wr.regime] || TONE.none} ${isOfficer ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
        title={wr.clause}
        data-testid="wage-regime-trigger">
        <Scale className="h-3 w-3" />
        <span className="font-semibold">Labor: {LABEL[wr.regime] || wr.regime}</span>
        <span className="opacity-70 ml-1">· {isOverride ? "Officer override" : conf}</span>
        {isOfficer && <Pencil className="h-2.5 w-2.5 opacity-60" />}
      </button>
      {wr.clause && <div className="text-[10px] text-zinc-400 mt-0.5 max-w-md" data-testid="wage-regime-clause">{wr.clause}</div>}

      {open && (
        <div className="absolute z-40 mt-1 w-80 bg-white border border-zinc-200 rounded-lg shadow-lg p-3" data-testid="wage-regime-editor">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-zinc-900">Override wage regime</span>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-700"><X className="h-4 w-4" /></button>
          </div>
          <p className="text-[10px] text-zinc-500 mb-2">Auto-detected: <span className="font-medium">{LABEL[wr.regime]}</span> ({conf}). Correcting a mis-detection is logged to audit &amp; the timeline and drives the labor basis.</p>
          <div className="space-y-1 mb-2">
            {REGIMES.map((r) => (
              <label key={r.v} className={`flex items-start gap-2 p-1.5 rounded-md cursor-pointer border ${regime === r.v ? "border-gold-dark bg-gold/5" : "border-transparent hover:bg-zinc-50"}`} data-testid={`wage-opt-${r.v}`}>
                <input type="radio" name="regime" checked={regime === r.v} onChange={() => setRegime(r.v)} className="mt-0.5" />
                <span><span className="text-xs font-medium text-zinc-900">{r.label}</span><span className="block text-[10px] text-zinc-500">{r.hint}</span></span>
              </label>
            ))}
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required, logged)" className="w-full bg-white border border-zinc-300 rounded px-2 py-1.5 text-xs text-zinc-900 outline-none focus:border-gold-dark mb-2" data-testid="wage-regime-reason" />
          <div className="flex items-center justify-between">
            {isOverride ? (
              <button onClick={clearOverride} disabled={busy} className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-800" data-testid="wage-regime-clear"><RotateCcw className="h-3 w-3" /> Revert to auto</button>
            ) : <span />}
            <button onClick={save} disabled={busy || !reason.trim()} className="text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50" data-testid="wage-regime-save">Save override</button>
          </div>
        </div>
      )}
    </div>
  );
}
